/**
 * Tetromino geometry.
 *
 * Shapes follow the Super Rotation System: each piece is a list of filled cells
 * per rotation state, expressed inside its own bounding box. Coordinates are
 * `[x, y]` with y increasing downward, matching the board grid and the screen.
 */

export type PieceType = "I" | "J" | "L" | "O" | "S" | "T" | "Z";

/** 0 = spawn, 1 = clockwise, 2 = flipped, 3 = counter-clockwise. */
export type Rotation = 0 | 1 | 2 | 3;

export type Cell = readonly [x: number, y: number];

export const PIECE_TYPES: readonly PieceType[] = ["I", "J", "L", "O", "S", "T", "Z"] as const;

/** Every rotation state of every piece, in SRS order. */
export const SHAPES: Record<PieceType, readonly (readonly Cell[])[]> = {
  I: [
    [[0, 1], [1, 1], [2, 1], [3, 1]],
    [[2, 0], [2, 1], [2, 2], [2, 3]],
    [[0, 2], [1, 2], [2, 2], [3, 2]],
    [[1, 0], [1, 1], [1, 2], [1, 3]],
  ],
  J: [
    [[0, 0], [0, 1], [1, 1], [2, 1]],
    [[1, 0], [2, 0], [1, 1], [1, 2]],
    [[0, 1], [1, 1], [2, 1], [2, 2]],
    [[1, 0], [1, 1], [0, 2], [1, 2]],
  ],
  L: [
    [[2, 0], [0, 1], [1, 1], [2, 1]],
    [[1, 0], [1, 1], [1, 2], [2, 2]],
    [[0, 1], [1, 1], [2, 1], [0, 2]],
    [[0, 0], [1, 0], [1, 1], [1, 2]],
  ],
  O: [
    [[0, 0], [1, 0], [0, 1], [1, 1]],
    [[0, 0], [1, 0], [0, 1], [1, 1]],
    [[0, 0], [1, 0], [0, 1], [1, 1]],
    [[0, 0], [1, 0], [0, 1], [1, 1]],
  ],
  S: [
    [[1, 0], [2, 0], [0, 1], [1, 1]],
    [[1, 0], [1, 1], [2, 1], [2, 2]],
    [[1, 1], [2, 1], [0, 2], [1, 2]],
    [[0, 0], [0, 1], [1, 1], [1, 2]],
  ],
  T: [
    [[1, 0], [0, 1], [1, 1], [2, 1]],
    [[1, 0], [1, 1], [2, 1], [1, 2]],
    [[0, 1], [1, 1], [2, 1], [1, 2]],
    [[1, 0], [0, 1], [1, 1], [1, 2]],
  ],
  Z: [
    [[0, 0], [1, 0], [1, 1], [2, 1]],
    [[2, 0], [1, 1], [2, 1], [1, 2]],
    [[0, 1], [1, 1], [1, 2], [2, 2]],
    [[1, 0], [0, 1], [1, 1], [0, 2]],
  ],
};

/**
 * Spawn column for each piece, chosen so the piece straddles the middle of a
 * ten-wide field the way the guideline specifies.
 */
export const SPAWN_X: Record<PieceType, number> = {
  I: 3,
  J: 3,
  L: 3,
  O: 4,
  S: 3,
  T: 3,
  Z: 3,
};

/** CSS custom property holding each piece's spot ink. */
export function pieceColorVar(type: PieceType): string {
  return `var(--color-piece-${type.toLowerCase()})`;
}

export function cellsFor(type: PieceType, rotation: Rotation): readonly Cell[] {
  const states = SHAPES[type];
  return states[rotation] ?? states[0]!;
}
